import {
  Grid,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
} from '@material-ui/core';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import { useFetcher } from '../hooks/useFetcher';
import { getProfile, updateTwoFactorAuthentication, verifyTwoFactorCode } from 'api/profile';

const TwoFactorAuthenticationContent = () => {
  const [is2faEnabled, setIs2faEnabled] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [enableOpen, setEnableOpen] = useState(false);
  const [code, setCode] = useState('');
  const [url2fa, setUrl2fa] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const { data } = useFetcher(getProfile);

  useEffect(() => {
    if (data) {
      const isEnabled = data.two_factor_enabled && data.two_factor_code;
      setIs2faEnabled(isEnabled);
    }
  }, [data]);

  const handle2faChange = async (event) => {
    const checked = event.target.checked;
    if (!checked) {
      setConfirmOpen(true);
    } else {
      let payload = {
        enabled: true,
      };
      try {
        const data = await updateTwoFactorAuthentication(payload);
        setUrl2fa(data.uri);
        setSecretKey(data.secret_key);
        setEnableOpen(true);
      } catch (error) {
        console.error('Error enabling 2FA:', error);
        toast.error('An error occurred while enabling 2FA');
      }
    }
  };

  const handleConfirmDisable = () => {
    setIs2faEnabled(false);
    setConfirmOpen(false);
    let payload = {
      enabled: false,
    };
    updateTwoFactorAuthentication(payload);
  };

  const handleCancelDisable = () => {
    setConfirmOpen(false);
  };

  const handleSaveEnable = async () => {
    let payload = {
      verification_code: code,
    };
    try {
      const response = await verifyTwoFactorCode(payload);
      setIs2faEnabled(true);
      setEnableOpen(false);
      setCode('');
      toast.success('Two-factor authentication enabled successfully.');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Wrong verification code. Please try again.');
        setCode('');
      } else {
        toast.error('Failed to enable two-factor authentication. Please try later.');
        setEnableOpen(false);
        setCode('');
      }
    }
  };

  const handleCancelEnable = () => {
    setEnableOpen(false);
    setCode('');
    let payload = {
      enabled: false,
    };
    updateTwoFactorAuthentication(payload);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormControlLabel
          control={<Switch color="secondary" checked={is2faEnabled} onChange={handle2faChange} />}
          label="Enable 2 Factor Authentication"
        />
      </Grid>

      {/* Disable Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDisable}
        aria-labelledby="confirm-disable-2fa"
        maxWidth="xs"
        fullWidth
      >
        {/* Close button */}
        <IconButton
          aria-label="close"
          onClick={handleCancelDisable}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#636d75',
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent style={{ textAlign: 'center', padding: '2rem' }}>
          <ErrorOutlineIcon style={{ fontSize: 80, color: '#f9b181', marginBottom: '1rem' }} />
          <Typography variant="h6" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Are you sure
          </Typography>
          <Typography variant="body1" style={{ color: '#636d75' }}>
            Do you want to disable 2 Factor Authentication?
          </Typography>
        </DialogContent>

        <DialogActions style={{ justifyContent: 'center', marginBottom: '1rem' }}>
          <Button
            onClick={handleConfirmDisable}
            variant="contained"
            style={{ backgroundColor: '#675ed5', color: '#fff', marginRight: 8 }}
          >
            OK
          </Button>
          <Button
            onClick={handleCancelDisable}
            variant="contained"
            style={{ backgroundColor: '#636d75', color: '#fff' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enable Setup Dialog */}
      <Dialog
        open={enableOpen}
        onClose={handleCancelEnable}
        aria-labelledby="enable-2fa"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="enable-2fa">2FA Verification</DialogTitle>

        <DialogContent>
          <Typography variant="body1" gutterBottom>
            1. Scan the following QR code using your authenticator app
          </Typography>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img src={url2fa} alt="QR Code" />
          </div>
          <Typography
            variant="body1"
            gutterBottom
            style={{ textAlign: 'center', marginBottom: '2rem' }}
          >
            Secret Key: <strong>{secretKey}</strong>
          </Typography>
          <Typography variant="body1" gutterBottom>
            2. Enter the code from your authenticator app
          </Typography>
          <TextField
            label="Code"
            variant="outlined"
            fullWidth
            margin="normal"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </DialogContent>

        <DialogActions style={{ justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelEnable} color="primary" variant="text">
            Cancel
          </Button>
          <Button onClick={handleSaveEnable} color="primary" variant="contained" disabled={!code}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default TwoFactorAuthenticationContent;
